import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocsFromServer,
  orderBy,
  limit,
  doc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { getDb, handleFirestoreError, OperationType, withRetry } from './firebase';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  body: string;
  category: string;
  featured_image_url: string;
  youtube_url?: string;
  seo_title?: string;
  meta_description?: string;
  status: 'draft' | 'published';
  published_at: Timestamp;
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Partial<T>;
}

export const getRefSource = () => {
  if (typeof window === 'undefined') return 'direct';
  const params = new URLSearchParams(window.location.search);
  return params.get('ref') || params.get('utm_source') || 'direct';
};

export const saveLead = async (leadData: any) => {
  const path = 'leads';
  try {
    const leadsRef = collection(getDb(), path);
    const docRef = await addDoc(leadsRef, {
      ...stripUndefined(leadData),
      ref_source: getRefSource(),
      status: 'new',
      created_at: serverTimestamp(),
      source_platform: 'web_contact_form'
    });
    return docRef.id;
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, path);
    throw error;
  }
};

export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  const path = 'blog_posts';
  try {
    const postsRef = collection(getDb(), path);
    const q = query(postsRef, where('slug', '==', slug), limit(1));
    const querySnapshot = await withRetry(() => getDocsFromServer(q));
    if (querySnapshot.empty) return null;
    const data = querySnapshot.docs[0].data();
    return { id: querySnapshot.docs[0].id, ...data } as BlogPost;
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, `${path}/${slug}`);
    return null;
  }
};

export const saveBooking = async (data: any) => {
  const path = 'bookings';
  try {
    const bookingsRef = collection(getDb(), path);
    const docRef = await addDoc(bookingsRef, {
      ...stripUndefined(data),
      ref_source: getRefSource(),
      status: 'pending',
      createdAt: serverTimestamp(),
      diaspora_priority: data.diaspora || false
    });
    return docRef.id;
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, path);
    throw error;
  }
};


export const getBlogPosts = async () => {
  const path = 'blog_posts';
  try {
    const postsRef = collection(getDb(), path);
    const q = query(
      postsRef,
      where('status', '==', 'published'),
      orderBy('published_at', 'desc')
    );
    const querySnapshot = await withRetry(() => getDocsFromServer(q));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as BlogPost[];
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, path);
    return [];
  }
};